import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, INSTAGRAM_URL } from "@/lib/api";
import { LogOut, User as UserIcon, Shield, Instagram } from "lucide-react";

const navLinks = [
  { to: "/", label: "Αρχική", id: "home" },
  { to: "/buy", label: "Αγορά", id: "buy" },
  { to: "/rent", label: "Ενοικίαση", id: "rent" },
  { to: "/contact", label: "Επικοινωνία", id: "contact" },
];

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl bg-[#050505]/85 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Vanguard Lux Cars" className="h-12 w-12 object-contain" />
          <div className="hidden sm:block leading-tight">
            <div className="font-display font-bold tracking-[0.2em] text-sm gold-gradient">
              VANGUARD
            </div>
            <div className="text-[0.6rem] tracking-[0.3em] text-gray-400 uppercase">
              Lux Cars
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.id}`}
              className={`font-display text-xs font-medium tracking-[0.2em] uppercase transition-colors ${
                loc.pathname === l.to ? "text-[var(--gold)]" : "text-gray-300 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {user && (
            <Link
              to="/my-inquiries"
              data-testid="nav-inquiries"
              className="font-display text-xs font-medium tracking-[0.2em] uppercase text-gray-300 hover:text-white"
            >
              Αιτήματα
            </Link>
          )}
          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="font-display text-xs font-medium tracking-[0.2em] uppercase text-[var(--gold)] hover:text-[var(--gold-bright)] flex items-center gap-1"
            >
              <Shield size={12} /> Διαχείριση
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-4">
          <span
            data-testid="greek-flag"
            className="hidden sm:flex h-9 w-9 items-center justify-center border border-white/10"
            title="Ελλάδα"
            aria-label="Greek flag"
          >
            <svg viewBox="0 0 27 18" className="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
              <rect width="27" height="18" fill="#0D5EAF"/>
              <rect y="2" width="27" height="2" fill="#FFFFFF"/>
              <rect y="6" width="27" height="2" fill="#FFFFFF"/>
              <rect y="10" width="27" height="2" fill="#FFFFFF"/>
              <rect y="14" width="27" height="2" fill="#FFFFFF"/>
              <rect width="10" height="10" fill="#0D5EAF"/>
              <rect x="4" width="2" height="10" fill="#FFFFFF"/>
              <rect y="4" width="10" height="2" fill="#FFFFFF"/>
            </svg>
          </span>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-instagram"
            className="hidden sm:flex h-9 w-9 items-center justify-center border border-white/10 hover:border-[var(--gold)] text-gray-300 hover:text-[var(--gold)] transition-all"
            title="Instagram"
          >
            <Instagram size={14} />
          </a>
          {user ? (
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="h-9 w-9 rounded-full border border-[var(--gold)]/40"
                  />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-[var(--surface-2)] flex items-center justify-center border border-[var(--gold)]/40">
                    <UserIcon size={14} className="text-[var(--gold)]" />
                  </div>
                )}
                <span className="text-xs text-gray-300 hidden lg:block">{user.name}</span>
              </div>
              <button
                data-testid="logout-btn"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="text-gray-400 hover:text-white p-2"
                title="Αποσύνδεση"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button
              data-testid="login-btn"
              onClick={handleLogin}
              className="btn-primary text-xs"
            >
              Σύνδεση
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
